import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { LabsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import useNavigate from "src/hooks/useNavigate";
import * as LaboratoryActions from "../../../actions/laboratories";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./LabsPage.scss";

interface LabsPageProps {
    formFilter: IFormFilter;
    formLength: number;
    laboratories: Dtos.Laboratory[];
    loadingLaboratories: boolean;
    loadLaboratoriesSuccess: boolean;
    loadLaboratoriesFailure: boolean;
    loadLaboratoriesErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearLaboratories: LaboratoryActions.ILaboratoryClearActionCreator;
    loadLaboratories: LaboratoryActions.ILaboratoriesLoadActionCreator;
    filter: LaboratoryActions.ILaboratoryFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const LabsPage: FC<LabsPageProps> = ({
    formFilter,
    laboratories,
    loadingLaboratories,
    loadLaboratoriesSuccess,
    loadLaboratoriesFailure,
    loadLaboratoriesErrorCode,
    permissions,
    pageSize,
    clearLaboratories,
    loadLaboratories,
    filter,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showLaboratorys = !loadingLaboratories && loadLaboratoriesSuccess;


    // Load Laboratorys
    useEffect(() => {
        if (!initalised) {
            setDrawerItems([]);
            setBreadcrumbs(LabsBreadcrumbs());

            loadLaboratories();

            setInitalised(true);
        }
    }, [
        initalised,
        setInitalised,
        laboratories,
        setDrawerItems,
        setBreadcrumbs,
        LabsBreadcrumbs,
        loadLaboratories,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadLaboratoriesFailure) {
            navigate(`/error/${loadLaboratoriesErrorCode}`);
        }
    }, [loadLaboratoriesFailure, loadLaboratoriesErrorCode, navigate]);

    // Cleanup effect to clear the laboratorys on component unmount
    useEffect(() => {
        return () => {
            clearLaboratories();
        };
    }, [clearLaboratories]);

    const createTable = useMemo(() => {
        if (!laboratories || laboratories.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>There are currently no labs to view.</span>
                    </div>
                </div>
            );
        }

        const showPagination = laboratories.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={laboratories}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : laboratories.length
                    }
                    pageSize={showPagination ? pageSize : laboratories.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no labs to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/labs/" + rowInfo.original.id + "/details";
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.FLASKVIAL}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                        {
                            id: "suburb",
                            Header: "Suburb",
                            accessor: "suburb",
                            maxWidth: 220,
                        },
                        {
                            id: "state",
                            Header: "State",
                            accessor: "state.value",
                            maxWidth: 220,
                        },
                        {
                            id: "country",
                            Header: "Country",
                            accessor: "country.value",
                            maxWidth: 220,
                        },
                    ]}
                />
            </div>
        );
    }, [laboratories, pageSize, PAGE_SIZE_OPTIONS, navigate, setPageSize]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Labs</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.LabCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/labs/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showLaboratorys ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showLaboratorys ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        AuthenticationHelper,
        permissions,
        showLaboratorys,
        createTable,
    ]);

    return (
        <RestrictedLayout
            loading={loadingLaboratories}
            permission={Dtos.Permission.LabView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<LabsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        laboratories: state.laboratories.formFilteredData!,
        formFilter: state.laboratories.formFilter,
        formLength: state.laboratories.formLength,
        loadingLaboratories:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Pending,
        loadLaboratoriesSuccess:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Success,
        loadLaboratoriesFailure:
            state.laboratories.loadState &&
            state.laboratories.loadState.status == RequestState.Failure,
        loadLaboratoriesErrorCode: state.laboratories.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<LabsPageProps> => {
    return {
        loadLaboratories: bindActionCreators(
            LaboratoryActions.LoadLaboratories,
            dispatch
        ),
        clearLaboratories: bindActionCreators(
            LaboratoryActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(LaboratoryActions.Filter, dispatch),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(LabsPage);
